import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import authApi from './auth-api.js';
import AuthModal from '../containers/auth-modal.jsx';
import {setSession, clearSession} from '../reducers/session.js';
import {selectLocale} from '../reducers/locales.js';

const messages = defineMessages({
    appTitle: {
        id: 'gui.session.appTitle',
        defaultMessage: 'Scratch',
        description: 'App title shown on the login gate screen'
    }
});

/**
 * HOC that checks for an existing session on mount.
 * If the user has valid auth cookies, it restores their session
 * and applies their saved locale preference.
 * If guest access is disabled, blocks unauthenticated users
 * with a login/register form.
 */
const SessionHOC = function (WrappedComponent) {
    class SessionWrapper extends React.Component {
        constructor (props) {
            super(props);
            this.state = {
                sessionChecked: false,
                guestBlocked: false
            };
            this.handleLoginSuccess = this.handleLoginSuccess.bind(this);
        }

        componentDidMount () {
            this.checkSession();
        }

        componentDidUpdate (prevProps) {
            // User logged out — re-check session to enforce auth gate
            if (prevProps.sessionUser && !this.props.sessionUser) {
                this.checkSession();
            }
        }

        async checkSession () {
            let hasSession = false;
            try {
                const user = await authApi.me();
                if (user) {
                    hasSession = true;
                    this.props.onSetSession(user);
                    // Restore user's saved locale from backend.
                    if (user.locale) {
                        let preferredLocale = user.locale;
                        try {
                            const saved = localStorage.getItem('scratch_locale');
                            if (saved && saved !== user.locale) {
                                preferredLocale = saved;
                                authApi.updateLocale(saved).catch(() => {});
                            }
                        } catch (_) { /* private browsing */ }

                        this.props.onSelectLocale(preferredLocale);
                        document.documentElement.lang = preferredLocale;
                        try {
                            localStorage.setItem('scratch_locale', preferredLocale);
                        } catch (_) { /* private browsing */ }
                    }
                }
            } catch {
                // No valid session — that's fine
            }

            // If no session, check if guests are allowed
            if (!hasSession) {
                const allowGuests = await authApi.getGuestAccess();
                if (!allowGuests) {
                    this.setState({guestBlocked: true});
                }
            }

            this.setState({sessionChecked: true});
        }

        handleLoginSuccess () {
            // User successfully logged in — unblock the editor
            this.setState({guestBlocked: false});
        }

        render () {
            const {
                intl,
                sessionUser, // eslint-disable-line no-unused-vars
                onSetSession, // eslint-disable-line no-unused-vars
                onSelectLocale, // eslint-disable-line no-unused-vars
                ...props
            } = this.props;

            // Don't render anything until session check is done
            if (!this.state.sessionChecked) {
                return null;
            }

            // Guest access disabled and user not logged in — show auth gate
            if (this.state.guestBlocked) {
                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100vh',
                        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                        background: 'linear-gradient(180deg, #4c97ff 0%, #3373cc 100%)'
                    }}>
                        <h1 style={{
                            color: 'white',
                            fontSize: '32px',
                            fontWeight: 700,
                            marginBottom: '24px',
                            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}>
                            {intl.formatMessage(messages.appTitle)}
                        </h1>
                        <AuthModal
                            gated
                            initialMode={'login'}
                            onClose={() => {}}
                            onLoginSuccess={this.handleLoginSuccess}
                        />
                    </div>
                );
            }

            return <WrappedComponent {...props} />;
        }
    }

    const mapStateToProps = state => ({
        sessionUser: state.session && state.session.session ? state.session.session.user : null
    });

    const mapDispatchToProps = dispatch => ({
        onSetSession: user => dispatch(setSession(user)),
        onSelectLocale: locale => dispatch(selectLocale(locale))
    });

    return connect(mapStateToProps, mapDispatchToProps)(injectIntl(SessionWrapper));
};

export default SessionHOC;
